import React, { useState, useEffect } from 'react';
import './TreatmentPlanManager.css';
import TreatmentPlanEditor from './TreatmentPlanEditor';
import { apiGet, apiPost } from '../../../services/api';
import Button from '../../common/Button';

interface TreatmentPlan {
    id: number;
    total_cost: number;
    discount: number;
    final_cost: number;
    status: string; // draft, proposed, accepted, completed, rejected
    notes: string;
    created_at: string;
    items: TreatmentPlanItem[];
}

interface TreatmentPlanItem {
    id: number;
    procedure_name: string;
    tooth_no: string;
    cost: number;
    status: string;
}

interface TreatmentPlanManagerProps {
    patientId: string;
    visitId?: number;
}

const TreatmentPlanManager: React.FC<TreatmentPlanManagerProps> = ({ patientId, visitId }) => {
    const [plans, setPlans] = useState<TreatmentPlan[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<TreatmentPlan | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (patientId) fetchPlans();
    }, [patientId]);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const data = await apiGet<TreatmentPlan[]>(`/api/treatment-plans/patient/${patientId}`);
            setPlans(data || []);
        } catch (err) {
            console.error("Failed to fetch plans", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setSelectedPlan(null);
        setIsEditing(true);
    };

    const handleEdit = (plan: TreatmentPlan) => {
        setSelectedPlan(plan);
        setIsEditing(true);
    };

    const handleSaveComplete = () => {
        setIsEditing(false);
        fetchPlans();
    };

    const handleAccept = async (planId: number) => {
        if (!window.confirm("Mark this plan as ACCEPTED?")) return;
        try {
            await apiPost(`/api/treatment-plans/${planId}/accept`, {});
            fetchPlans();
        } catch (e) {
            console.error(e);
            alert("Failed to accept plan");
        }
    };

    const handlePrint = (plan: TreatmentPlan) => {
        const win = window.open('', '_blank');
        if (!win) return;
        win.document.write(`
            <html>
            <head>
                <title>Treatment Plan</title>
                <style>
                    body { font-family: sans-serif; padding: 20px; line-height: 1.5; }
                    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #eee; padding-bottom: 15px; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f8f9fa; }
                    .totals { text-align: right; margin-top: 20px; font-size: 1.1em; }
                    .notes { margin-top: 30px; font-style: italic; color: #555; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h2>Proposed Treatment Plan</h2>
                    <p>Date: ${new Date(plan.created_at).toLocaleDateString()}</p>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Tooth</th>
                            <th>Procedure</th>
                            <th style="text-align: right">Cost (INR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${plan.items.map(item => `
                            <tr>
                                <td>${item.tooth_no ? `Tooth ${item.tooth_no}` : 'General'}</td>
                                <td>${item.procedure_name}</td>
                                <td style="text-align: right">${item.cost}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="totals">
                    <p>Subtotal: ₹${plan.total_cost}</p>
                    ${plan.discount > 0 ? `<p>Discount: ₹${plan.discount}</p>` : ''}
                    <p><strong>Final Estimate: ₹${plan.final_cost}</strong></p>
                </div>
                ${plan.notes ? `<div class="notes"><strong>Notes:</strong><br/>${plan.notes}</div>` : ''}
                <script>
                    window.onload = function() { window.print(); window.close(); }
                </script>
            </body>
            </html>
        `);
        win.document.close();
    };

    if (isEditing) {
        return (
            <TreatmentPlanEditor
                patientId={patientId}
                visitId={visitId}
                existingPlan={selectedPlan}
                onSave={handleSaveComplete}
                onCancel={() => setIsEditing(false)}
            />
        );
    }

    return (
        <div className="treatment-plan-manager">
            <div className="tpm-header">
                <h3>Treatment Plans</h3>
                <Button variant="primary" onClick={handleCreateNew}>+ New Plan</Button>
            </div>

            {loading && <div className="loading">Loading plans...</div>}

            {!loading && plans.length === 0 && (
                <div className="tpm-empty">No treatment plans found. Create one to get started.</div>
            )}

            <div className="tpm-list">
                {plans.map(plan => (
                    <div key={plan.id} className={`tpm-card status-${plan.status}`}>
                        <div className="tpm-card-header">
                            <span className="tpm-date">{new Date(plan.created_at).toLocaleDateString()}</span>
                            <span className={`tpm-status status-badge-${plan.status}`}>{plan.status.toUpperCase()}</span>
                        </div>
                        <div className="tpm-items">
                            {plan.items.map(item => (
                                <div key={item.id} className="tpm-item-row">
                                    <span className="tpm-tooth">{item.tooth_no ? `Tooth ${item.tooth_no}` : 'General'}</span>
                                    <span className="tpm-proc">{item.procedure_name}</span>
                                    <span className="tpm-cost">₹{item.cost}</span>
                                </div>
                            ))}
                        </div>
                        <div className="tpm-footer">
                            <div className="tpm-total">
                                Total: <strong>₹{plan.final_cost}</strong>
                            </div>
                            <div className="tpm-actions">
                                {plan.status === 'draft' && <Button variant="outline" size="sm" onClick={() => handleEdit(plan)}>Edit</Button>}
                                {plan.status === 'draft' && <Button variant="primary" size="sm" onClick={() => handleAccept(plan.id)}>Accept</Button>}
                                {plan.status === 'accepted' && (
                                    <>
                                        <span className="accepted-label">✓ Active</span>
                                        <Button variant="outline" size="sm" onClick={() => handlePrint(plan)}>🖨️ Print</Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TreatmentPlanManager;
